/* global fetch */
import { PYTHON_BACKEND_URL } from "@carousel-office/shared";
import { getAgentServices } from "../../services";
export const BACKEND_BASE_URL = PYTHON_BACKEND_URL;
function getAuthHeader() {
    const token = getAgentServices().getJwtToken?.();
    if (token && token.trim().length > 0) {
        return `Bearer ${token}`;
    }
    return undefined;
}
function unwrapQuotedSelector(value) {
    const trimmed = value.trim();
    if (trimmed.length >= 2) {
        const quoteWrapped = (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
            (trimmed.startsWith("'") && trimmed.endsWith("'"));
        if (quoteWrapped) {
            return trimmed.slice(1, -1).trim();
        }
    }
    if (trimmed.length >= 4) {
        const escapedQuoteWrapped = (trimmed.startsWith('\\"') && trimmed.endsWith('\\"')) ||
            (trimmed.startsWith("\\'") && trimmed.endsWith("\\'"));
        if (escapedQuoteWrapped) {
            return trimmed.slice(2, -2).trim();
        }
    }
    return trimmed;
}
function normalizeDocumentSelector(value) {
    let normalized = value.trim();
    for (let i = 0; i < 3; i++) {
        const unwrapped = unwrapQuotedSelector(normalized);
        if (unwrapped === normalized)
            break;
        normalized = unwrapped;
    }
    if (normalized.toUpperCase() === "CURRENT")
        return "CURRENT";
    if (normalized.toUpperCase() === "ALL")
        return "ALL";
    return normalized;
}
function buildUrlWithQuery(path, query) {
    if (!query || Object.keys(query).length === 0) {
        return `${BACKEND_BASE_URL}${path}`;
    }
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (typeof value === "string" && value.length > 0) {
            params.set(key, value);
        }
    }
    const queryString = params.toString();
    return queryString.length > 0
        ? `${BACKEND_BASE_URL}${path}?${queryString}`
        : `${BACKEND_BASE_URL}${path}`;
}
async function requestJson(args) {
    const method = args.method || "POST";
    const headers = {
        "Content-Type": "application/json",
    };
    if (args.requireAuth) {
        const auth = getAuthHeader();
        if (auth) {
            headers.Authorization = auth;
        }
    }
    const response = await fetch(buildUrlWithQuery(args.path, args.query), {
        method,
        headers,
        ...(args.payload ? { body: JSON.stringify(args.payload) } : {}),
    });
    if (!response.ok) {
        throw new Error(`Backend error (${response.status}): ${await response.text()}`);
    }
    return response.json();
}
async function postJson(path, payload, requireAuth = false) {
    return requestJson({
        path,
        method: "POST",
        payload,
        requireAuth,
    });
}
export const backendClient = {
    async syncSettings(settings) {
        try {
            await requestJson({
                path: "/api/settings/sync",
                method: "POST",
                payload: settings,
            });
        }
        catch (error) {
            console.debug("[agent-runtime/backendClient] Settings sync unavailable:", error);
        }
    },
    async getCustomInstructions(mode) {
        const response = await requestJson({
            path: "/api/settings/custom-instructions",
            method: "GET",
            query: { mode },
        });
        return response.customInstructions || {
            carouselChat: [],
            search: [],
            research: [],
            execution: [],
        };
    },
    async updateCustomInstructions(mode, customInstructions) {
        const response = await requestJson({
            path: "/api/settings/custom-instructions",
            method: "PUT",
            query: { mode },
            payload: customInstructions,
        });
        return response.customInstructions || customInstructions;
    },
    async getExcelColors() {
        const response = await requestJson({
            path: "/api/settings/excel-colors",
            method: "GET",
        });
        return (response.excelColors || {
            isExcelSemanticFormattingEnabled: true,
            excelApplyColorsOnlyToEmptyCells: true,
            excelHardcodedFontColor: "#0000FF",
            excelCalculationFontColor: "#000000",
            excelReferenceOtherSheetFontColor: "#1E914E",
            excelReferenceOtherWorkbookFontColor: "#8B0000",
            excelReferenceExternalDataFontColor: "#E30000",
        });
    },
    async updateExcelColors(colors) {
        const response = await requestJson({
            path: "/api/settings/excel-colors",
            method: "PUT",
            payload: colors,
        });
        return response.excelColors || colors;
    },
    async alphaSenseSearchBatch(queries) {
        return postJson("/alphasense/search/batch", { queries });
    },
    async searchImages(documentId, query, limit = 10) {
        try {
            const normalizedDocumentId = normalizeDocumentSelector(documentId);
            if (normalizedDocumentId !== documentId) {
                console.warn(`[agent-runtime/backendClient] Normalized searchImages documentId from "${documentId}" to "${normalizedDocumentId}"`);
            }
            const unified = await postJson("/search/unified", {
                deck_id: normalizedDocumentId,
                search_type: "semantic",
                queries: [query],
                result_types: ["asset"],
                num_results: Math.max(limit * 2, limit),
                enrich_results: false,
            }, true);
            const raw = Array.isArray(unified.results) ? unified.results : [];
            const results = raw
                .filter((item) => {
                const metadata = item.metadata && typeof item.metadata === "object"
                    ? item.metadata
                    : {};
                const assetKind = item.asset_kind ?? metadata.asset_kind;
                const shapeType = item.shape_type ?? metadata.shape_type;
                return (assetKind === "image" ||
                    shapeType === "picture" ||
                    shapeType === "image" ||
                    item.has_image === true);
            })
                .slice(0, limit)
                .map((item) => {
                const metadata = item.metadata && typeof item.metadata === "object"
                    ? item.metadata
                    : {};
                return {
                    slide_id: String(item.slide_id ?? item.slideId ?? ""),
                    shape_id: String(item.shape_id ?? item.shapeId ?? metadata.shape_id ?? item.result_id ?? ""),
                    description: String(item.description ?? item.text ?? item.page_content ?? ""),
                    similarity: Number(item.score ?? item.similarity ?? 0),
                    shape_type: String(item.shape_type ?? metadata.shape_type ?? "picture"),
                    has_image: true,
                    image_base64: typeof item.image_base64 === "string" ? item.image_base64 : undefined,
                };
            });
            return {
                success: true,
                results,
                total_found: results.length,
            };
        }
        catch (error) {
            return {
                success: false,
                results: [],
                total_found: 0,
                error: error instanceof Error ? error.message : "Unknown error",
            };
        }
    },
    async resolveContext(contextId) {
        try {
            return await postJson("/context/resolve", {
                contextId,
            }, true);
        }
        catch (error) {
            return {
                success: false,
                contextId,
                resultType: "unknown",
                presentationId: "",
                isResolved: false,
                error: error instanceof Error ? error.message : "Unknown error",
            };
        }
    },
    async saveAssetToFile(assetId, base64, mimeType) {
        try {
            return await postJson("/debug/assets/save", {
                asset_id: assetId,
                base64,
                mime_type: mimeType,
            });
        }
        catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : "Unknown error",
            };
        }
    },
};
